import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { useAuth } from "./context/AuthContext";
import BackArrow from "./components/GeneratePage/BackArrow";
import AddDemoDialog from "./components/AddDemoDialog";
import api from "./services/api";
import LoadingIcon from "./components/GeneratePage/LoadingIcon";

interface HookVideo {
  id: string;
  video_url: string;
  thumbnail_url?: string;
  hook_text: string;
  text_position: string;
  created_at: string;
  status: string;
  video_type?: string; // "background" or "hook"
}

const HookVideosPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Add state for hook text
  const [hookText, setHookText] = useState<string>("");
  const maxCharacters = 220; // Character limit for hook text

  // Add state for text position (top, middle, bottom)
  const [textPosition, setTextPosition] = useState<"top" | "middle" | "bottom">(
    "middle"
  );

  // Add state for pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // Add state for hook videos
  const [hookVideos, setHookVideos] = useState<HookVideo[]>([]);
  const [backgroundVideos, setBackgroundVideos] = useState<HookVideo[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<HookVideo | null>(null);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState<boolean>(false);
  const [generationProgress, setGenerationProgress] = useState<number>(0);
  const [generationError, setGenerationError] = useState<string>("");
  const [generatingVideoId, setGeneratingVideoId] = useState<string | null>(null);

  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0, jobId: null as string | null });

  // Confirmation dialog state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add state for AddDemoDialog
  const [isAddDemoDialogOpen, setIsAddDemoDialogOpen] = useState(false);
  const [demos, setDemos] = useState<{ name: string; videoUrl: string }[]>([]);

  // Handle adding a new demo
  const handleAddDemo = (videos: File[]) => {
    if (videos.length > 0) {
      // Create an object URL for the video preview
      const videoUrl = URL.createObjectURL(videos[0]);
      // Use the filename as the name
      const name = videos[0].name;
      setDemos([...demos, { name, videoUrl }]);
    }
  };

  // Fetch hook videos from API
  const fetchHookVideos = async () => {
    try {
      setIsLoading(true);
      setError("");

      const pageSize = 16; // Number of videos per page (increased from 4 to 16)
      const response = await api.get(`/api/hook/list-hook-videos?page=${currentPage}&page_size=${pageSize}&video_type=hook`);

      if (response.data && response.data.items) {
        const videos = response.data.items;
        setHookVideos(videos);
        setTotalPages(response.data.pages || 1);

        // Don't set hook videos as the selected video by default
  // This prevents hook videos from showing in the preview
      } else {
        console.warn("Unexpected response format:", response.data);
        setHookVideos([]);
        setTotalPages(1);
      }
    } catch (err: any) {
      console.error("Error fetching hook videos:", err);

      // Provide more detailed error information based on the error type
      if (err.response) {
        // The server responded with an error status
        setError(err.response.data?.detail || `Server error (${err.response.status})`);
      } else if (err.request) {
        // The request was made but no response was received
        setError("No response from server. Please check your connection and try again.");
      } else {
        // Something happened in setting up the request
        setError(err.message || "Failed to load hook videos");
      }

      // Clear videos on error to show empty state
      setHookVideos([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle video selection - only allow selecting background videos
  const handleVideoSelect = (video: HookVideo) => {
    // Only allow selecting videos with video_type "background"
    if (video.video_type === "background") {
      setSelectedVideo(video);
    }
  };

  // Generate hook video with text overlay
  const handleGenerateHook = async () => {
    try {
      // Validate inputs
      if (!selectedVideo && demos.length === 0) {
        setError("Please select a video or add a demo first");
        return;
      }

      if (hookText.trim() === "") {
        setError("Please enter hook text");
        return;
      }

      if (hookText.length > maxCharacters) {
        setError(`Hook text exceeds maximum length of ${maxCharacters} characters`);
        return;
      }

      setIsGeneratingVideo(true);
      setGenerationError("");
      setGenerationProgress(10);

      // Create the payload for the API
      const payload = {
        video_url: selectedVideo ? selectedVideo.video_url : demos[0].videoUrl,
        hook_text: hookText,
        text_position: textPosition,
        video_type: "hook" // Set video type to 'hook' for all generated videos from this page
      };

      // Make API call to queue the job
      const response = await api.post("/api/hook/generate", payload);

      if (response.data && response.data.id) {
        const jobId = response.data.id;
        setGeneratingVideoId(jobId);

        // Display the job immediately in the list with a "processing" status
        const newVideoJob: HookVideo = {
          id: jobId,
          video_url: selectedVideo ? selectedVideo.video_url : demos[0].videoUrl,
          hook_text: hookText,
          text_position: textPosition,
          created_at: new Date().toISOString(),
          status: "processing",
          video_type: "hook" // Make sure this is set to "hook" for new videos
        };

        // Add the new job to the top of the list
        setHookVideos(prev => [newVideoJob, ...prev]);

        // Start polling for job status
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await api.get(`/api/hook/status/${jobId}`);

            if (statusResponse.data) {
              const { status, progress, video_url } = statusResponse.data;

              // Update the progress
              setGenerationProgress(progress || 0);

              // Update the video status and URL in the list
              setHookVideos(prev =>
                prev.map(video =>
                  video.id === jobId
                    ? {
                        ...video,
                        status,
                        video_url: video_url || video.video_url,
                        // Make sure hook_text and text_position are preserved
                        hook_text: hookText || video.hook_text,
                        text_position: textPosition || video.text_position
                      }
                    : video
                )
              );

              // Check if job is completed or failed
              if (status === "completed" || status === "failed") {
                clearInterval(pollInterval);
                setIsGeneratingVideo(false);
                setGeneratingVideoId(null);

                if (status === "failed") {
                  setGenerationError("Video generation failed");
                } else {
                  // Refresh the list to get the new video
                  fetchHookVideos();
                }
              }
            }
          } catch (error) {
            console.error("Error polling job status:", error);
          }
        }, 3000); // Poll every 3 seconds

        return () => clearInterval(pollInterval);
      }
    } catch (err: any) {
      console.error("Error generating hook video:", err);
      setIsGeneratingVideo(false);
      setGenerationError(err.response?.data?.detail || "Failed to generate hook video");
    }
  };

  // Delete a hook video
  const handleDeleteVideo = async (videoId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent video selection when clicking delete button

    try {
      await api.delete(`/api/hook/delete/${videoId}`);

      // Remove the video from the list
      setHookVideos(prev => prev.filter(video => video.id !== videoId));

      // If the deleted video was selected, clear the selection
      if (selectedVideo && selectedVideo.id === videoId) {
        setSelectedVideo(null);
      }
    } catch (err: any) {
      console.error("Error deleting hook video:", err);
      setError(err.response?.data?.detail || "Failed to delete hook video");
    }
  };

  // Function to handle right-click on a job card
  const handleContextMenu = (e: React.MouseEvent, jobId: string) => {
    e.preventDefault(); // Prevent default browser context menu

    // Set context menu position and job id
    setContextMenuPosition({
      x: e.clientX,
      y: e.clientY,
      jobId: jobId,
    });

    setShowContextMenu(true);
  };

  // Function to delete a video job
  const deleteVideoJob = async (jobId: string) => {
    if (!jobId) return;

    setIsDeleting(true);

    try {
      const response = await api.delete(`/api/hook/jobs/${jobId}`);

      if (response.status === 200) {
        // Remove the job from the state
        setHookVideos(prev => prev.filter(video => video.id !== jobId));

        // If the deleted job was selected, clear the selection
        if (selectedVideo && selectedVideo.id === jobId) {
          setSelectedVideo(null);
        }

        // Show success message
        setError(""); // Clear any existing errors
      }
    } catch (err: any) {
      console.error("Error deleting video job:", err);
      setError(err.response?.data?.detail || "Failed to delete video job");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
      setJobToDelete(null);
    }
  };

  // Handle clicking outside the context menu to close it
  useEffect(() => {
    const handleClickOutside = () => {
      setShowContextMenu(false);
    };

    if (showContextMenu) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showContextMenu]);

  // Fetch background videos for AI Avatar section
  const fetchBackgroundVideos = async () => {
    try {
      const response = await api.get(`/api/hook/list-hook-videos?page=1&page_size=16&video_type=background`);

      if (response.data && response.data.items) {
        const videos = response.data.items;
        setBackgroundVideos(videos);

        // Set the first background video as selected by default
        if (videos.length > 0) {
          setSelectedVideo(videos[0]);
        }
      } else {
        console.warn("Unexpected response format for background videos:", response.data);
        setBackgroundVideos([]);
      }
    } catch (err) {
      console.error("Error fetching background videos:", err);
    }
  };

  // Load videos when component mounts or page changes
  useEffect(() => {
    fetchHookVideos();
    fetchBackgroundVideos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <div className="container mx-auto p-4 pb-24">
      {/* Error display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative">
          <span className="block sm:inline">{error}</span>
          <span
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setError("")}
          >
            <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <title>Close</title>
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
            </svg>
          </span>
        </div>
      )}

      {/* Navigation Header */}
      <div className="border-gray-200 dark:border-gray-800 py-3 mb-6 bg-white dark:bg-gray-900 transition-colors duration-200 px-4 rounded-xl border border-b-3">
        <div className="flex items-center justify-between">
          {/* Back button */}
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200 hover:cursor-pointer"
          >
            <BackArrow />
            Back
          </button>

          {/* Title (centered) */}
          <div className="flex-1 flex justify-center">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Hook Videos
            </h1>
          </div>

          {/* Credits indicator */}
          <div className="flex items-center justify-center">
            <Link
              to="/pricing"
              className="border border-gray-300 px-3 py-1 flex flex-row items-center rounded-md hover:cursor-pointer relative pb-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                className="h-4 w-4 fill-yellow-500 mr-1"
              >
                {/*!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}
                <path d="M512 80c0 18-14.3 34.6-38.4 48c-29.1 16.1-72.5 27.5-122.3 30.9c-3.7-1.8-7.4-3.5-11.3-5C300.6 137.4 248.2 128 192 128c-8.3 0-16.4 .2-24.5 .6l-1.1-.6C142.3 114.6 128 98 128 80c0-44.2 86-80 192-80S512 35.8 512 80zM160.7 161.1c10.2-.7 20.7-1.1 31.3-1.1c62.2 0 117.4 12.3 152.5 31.4C369.3 204.9 384 221.7 384 240c0 4-.7 7.9-2.1 11.7c-4.6 13.2-17 25.3-35 35.5c0 0 0 0 0 0c-.1 .1-.3 .1-.4 .2c0 0 0 0 0 0s0 0 0 0c-.3 .2-.6 .3-.9 .5c-35 19.4-90.8 32-153.6 32c-59.6 0-112.9-11.3-148.2-29.1c-1.9-.9-3.7-1.9-5.5-2.9C14.3 274.6 0 258 0 240c0-34.8 53.4-64.5 128-75.4c10.5-1.5 21.4-2.7 32.7-3.5zM416 240c0-21.9-10.6-39.9-24.1-53.4c28.3-4.4 54.2-11.4 76.2-20.5c16.3-6.8 31.5-15.2 43.9-25.5l0 35.4c0 19.3-16.5 37.1-43.8 50.9c-14.6 7.4-32.4 13.7-52.4 18.5c.1-1.8 .2-3.5 .2-5.3zm-32 96c0 18-14.3 34.6-38.4 48c-1.8 1-3.6 1.9-5.5 2.9C304.9 404.7 251.6 416 192 416c-62.8 0-118.6-12.6-153.6-32C14.3 370.6 0 354 0 336l0-35.4c12.5 10.3 27.6 18.7 43.9 25.5C83.4 342.6 135.8 352 192 352s108.6-9.4 148.1-25.9c7.8-3.2 15.3-6.9 22.4-10.9c6.1-3.4 11.8-7.2 17.2-11.2c1.5-1.1 2.9-2.3 4.3-3.4l0 3.4 0 5.7 0 26.3zm32 0l0-32 0-25.9c19-4.2 36.5-9.5 52.1-16c16.3-6.8 31.5-15.2 43.9-25.5l0 35.4c0 10.5-5 21-14.9 30.9c-16.3 16.3-45 29.7-81.3 38.4c.1-1.7 .2-3.5 .2-5.3zM192 448c56.2 0 108.6-9.4 148.1-25.9c16.3-6.8 31.5-15.2 43.9-25.5l0 35.4c0 44.2-86 80-192 80S0 476.2 0 432l0-35.4c12.5 10.3 27.6 18.7 43.9 25.5C83.4 438.6 135.8 448 192 448z" />
              </svg>
              <span>{user?.videos_left} credits left</span>
              <span className="ml-2 bg-gray-200 rounded-md bg-gradient-to-b from-purple-700 to-purple-400 border-2 border-b-3 border-purple-800 fill-white hover:cursor-pointer p-0.5">
                <PlusIcon />
              </span>
              {/* Credit progress bar */}
              <div className="absolute bottom-0 left-0 w-full h-1 overflow-hidden rounded-b-md">
                <div
                  className="h-full bg-blue-500 shadow-[0_0_5px_2px_rgba(59,130,246,0.5)] transition-all duration-300 ease-in-out"
                  style={{ width: `${((user?.videos_left || 0) / (user?.max_videos || 20)) * 100}%` }}
                ></div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content - Grid Layout */}
      <div className="flex flex-col md:flex-row overflow-hidden rounded-lg shadow-md">
        {/* Left Side - Content Controls */}
        <div className="flex flex-col w-full md:w-1/2">
          {/* Combined Section for Hook, AI Avatar, and Sound */}
          <div className="bg-white dark:bg-gray-900 p-6 flex flex-col space-y-5 h-full">
            {/* Hook Section */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-semibold tracking-normal text-gray-900 dark:text-white">
                  Hook
                </h2>
                <p
                  className={`text-sm ${
                    hookText.length > maxCharacters
                      ? "text-red-500 font-bold"
                      : "text-purple-500 dark:text-purple-400"
                  }`}
                >
                  {hookText.length}/{maxCharacters}
                </p>
              </div>
              <textarea
                value={hookText}
                onChange={(e) => setHookText(e.target.value)}
                className={`w-full p-2.5 border-2 ${
                  hookText.length > maxCharacters
                    ? "border-red-300 dark:border-red-600 focus:ring-red-500/50 dark:focus:ring-red-500/30"
                    : "border-purple-300 dark:border-purple-700 focus:ring-purple-500/50 dark:focus:ring-purple-500/30"
                } rounded-lg focus:outline-none focus:ring-4 resize-none shadow-[0_0_10px_rgba(168,85,247,0.35)] dark:shadow-[0_0_10px_rgba(168,85,247,0.25)] h-20 bg-white dark:bg-gray-950 text-gray-900 dark:text-white transition-colors duration-200`}
                placeholder="Enter your hook text here..."
              />
            </div>

            {/* AI Avatar Section */}
            <div>
              <h2 className="text-lg font-semibold tracking-normal text-gray-900 dark:text-white mb-2">
                AI Avatar
              </h2>
              <div className="flex mb-3 border-b border-gray-200 dark:border-gray-700">
                <button className="px-3 py-1.5 text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border-b-2 border-purple-500 dark:border-purple-400 rounded-t-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  Default
                </button>
                <button className="px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-md transition-colors">
                  My UGC
                </button>
                <button className="px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-md transition-colors">
                  Uploads
                </button>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1">
                {/* Show background videos from AI Avatar section */}
                {backgroundVideos.map((video, index) => (
                  <div
                    key={video.id}
                    className={`aspect-square w-full rounded-md transform hover:scale-105 transition-transform cursor-pointer relative overflow-hidden ${
                      selectedVideo?.id === video.id ? 'ring-2 ring-purple-500' : ''
                    }`}
                    onClick={() => handleVideoSelect(video)}
                  >
                    {video.thumbnail_url ? (
                      <img
                        src={video.thumbnail_url}
                        alt="Background video thumbnail"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center"
                      >
                        <video
                          src={video.video_url}
                          className="w-full h-full object-cover"
                          preload="metadata"
                        />
                      </div>
                    )}
                    {/* Video status indicator */}
                    {video.status !== "completed" && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Empty placeholders if less than 16 videos */}
                {Array.from({ length: Math.max(0, 16 - backgroundVideos.length) }).map((_, i) => (
                  <div
                    key={`empty-${i}`}
                    className="aspect-square w-full bg-gray-200 dark:bg-gray-700 rounded-md opacity-40"
                  ></div>
                ))}
              </div>
            </div>

            {/* Demos Section - Replacing Sound Section */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-semibold tracking-normal text-gray-900 dark:text-white">
                  Demos
                </h2>
              </div>
              {/* Demo options with "Add New Demo" button */}
              <div className="flex h-24">
                {/* Add New Demo button */}
                <div className="aspect-[9/16] h-full">
                  <button
                    onClick={() => setIsAddDemoDialogOpen(true)}
                    className="flex flex-col items-center justify-center bg-white dark:bg-gray-900 border-2 border-blue-400 dark:border-blue-500 rounded-lg w-full h-full transition-colors duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    <PlusCircleIcon className="w-6 h-6 text-blue-500 dark:text-blue-400" />
                  </button>
                </div>

                {/* Display added demos */}
                {demos.map((demo, index) => (
                  <div key={index} className="aspect-[9/16] h-full ml-2">
                    <div className="relative w-full h-full rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700">
                      <video
                        src={demo.videoUrl}
                        className="w-full h-full object-cover"
                        muted
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-1">
                        <p className="text-white text-xs truncate">
                          {demo.name}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Generate Hook Button with Sound Button to the left */}
            <div className="flex space-x-2 mt-3">
              <button className="rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 px-4 py-2.5 font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 cursor-pointer transition-colors duration-200">
                Test Sound
              </button>
              <button
                onClick={handleGenerateHook}
                disabled={isGeneratingVideo}
                className={`flex-1 rounded-lg px-4 py-2.5 font-bold text-white border border-black dark:border-gray-900 border-b-2 cursor-pointer transition-all duration-200 flex items-center justify-center ${
                  isGeneratingVideo
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-b from-purple-600 to-purple-400 dark:from-purple-500 dark:to-purple-700 hover:opacity-90"
                }`}
              >
                {isGeneratingVideo ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating ({generationProgress}%)
                  </>
                ) : (
                  "Generate Hook"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right Side - Video Preview */}
        <div className="flex flex-col space-y-5 bg-white dark:bg-gray-900 p-6 w-full md:w-1/2">
          {/* Mobile Phone Frame */}
          <div className="mx-auto relative flex-1 flex justify-center items-center">
            <div className="bg-black rounded-3xl overflow-hidden w-72 aspect-[9/16] shadow-lg border-4 border-gray-800 relative">
              {/* Video background */}
              {selectedVideo ? (
                <video
                  src={selectedVideo.video_url}
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  onError={(e) => {
                    console.error("Video playback error:", e);
                    setError("Error playing video. The video may be unavailable or in an unsupported format.");
                  }}
                />
              ) : (
                <div
                  className="absolute inset-0 bg-gradient-to-b from-blue-500 to-purple-600 opacity-50"
                  style={{
                    backgroundImage:
                      "url('https://placehold.co/400x720/333/111')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              )}

              {/* Video overlay elements */}
              <div className="absolute inset-0 flex flex-col">
                {/* Top bar with fake UI elements */}
                <div className="flex justify-between items-center p-2">
                  <div className="w-6 h-6 rounded-full bg-white/20"></div>
                  <div className="w-14 h-4 rounded-full bg-white/20"></div>
                </div>

                {/* Bottom bar with fake UI elements */}
                <div className="mt-auto p-2 flex justify-between items-center">
                  <div className="w-16 h-4 rounded-full bg-white/20"></div>
                  <div className="w-6 h-6 rounded-full bg-white/20"></div>
                </div>
              </div>

              {/* Hook text overlay - positioning based on textPosition state */}
              <div
                className={`absolute inset-x-0 px-4 z-10 flex items-center justify-center
                  ${
                    textPosition === "top"
                      ? "top-24 bottom-auto"
                      : textPosition === "bottom"
                      ? "top-auto bottom-24"
                      : "inset-y-0"
                  }`}
              >
                <p className="text-white text-xl font-bold text-center">
                  {hookText || "Your hook text will appear here"}
                </p>
              </div>

              {/* Controls overlaid on the video */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-3 z-20">
                {/* Top position button */}
                <button
                  className={`p-1.5 rounded-md backdrop-blur-sm 
                    ${
                      textPosition === "top"
                        ? "bg-purple-500/80 text-white"
                        : "bg-gray-100/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300"
                    } 
                    hover:bg-gray-200 dark:hover:bg-gray-700`}
                  onClick={() => setTextPosition("top")}
                  title="Position text at top"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-5 h-5"
                  >
                    <path d="M4 5h16v2H4zM4 9h16v2H4zM4 17h16v2H4z" />
                  </svg>
                </button>

                {/* Middle position button */}
                <button
                  className={`p-1.5 rounded-md backdrop-blur-sm
                    ${
                      textPosition === "middle"
                        ? "bg-purple-500/80 text-white"
                        : "bg-gray-100/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300"
                    } 
                    hover:bg-gray-200 dark:hover:bg-gray-700`}
                  onClick={() => setTextPosition("middle")}
                  title="Position text in middle"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-5 h-5"
                  >
                    <path d="M4 5h16v2H4zM4 11h16v2H4zM4 17h16v2H4z" />
                  </svg>
                </button>

                {/* Bottom position button */}
                <button
                  className={`p-1.5 rounded-md backdrop-blur-sm
                    ${
                      textPosition === "bottom"
                        ? "bg-purple-500/80 text-white"
                        : "bg-gray-100/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300"
                    } 
                    hover:bg-gray-200 dark:hover:bg-gray-700`}
                  onClick={() => setTextPosition("bottom")}
                  title="Position text at bottom"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-5 h-5"
                  >
                    <path d="M4 5h16v2H4zM4 13h16v2H4zM4 17h16v2H4z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Your Generated Hooks Section */}
      <div className="mt-8">
        {/* Content area with white background and shadow */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-md p-6">
          {/* Title and Download button */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Your Generated Hook Videos
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                These are the ones you have already created
              </p>
            </div>
            <button className="rounded-lg bg-blue-500 hover:bg-blue-600 px-4 py-2 text-white font-medium transition-colors duration-200">
              Download
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5 ml-1 inline-block"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          {/* Grid of Generated Hooks */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {isLoading ? (
              // Loading placeholders
              [...Array(12)].map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className="aspect-[9/16] bg-gray-200 dark:bg-gray-700 rounded-xl overflow-hidden relative animate-pulse"
                />
              ))
            ) : hookVideos.length > 0 ? (
              // Actual hook videos
              hookVideos.map((video) => (
                <div
                  key={video.id}
                  className="rounded-lg overflow-hidden transition-all hover:shadow-lg border border-gray-200 dark:border-gray-700 shadow-md dark:bg-gray-800 relative"
                  onContextMenu={(e) => handleContextMenu(e, video.id)}
                >
                  {video.status === "completed" ? (
                    <>
                      <div className="aspect-[9/16] bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden">
                        <video
                          controls
                          src={video.video_url}
                          className="w-full h-full object-cover"
                          poster={video.thumbnail_url || undefined}
                          preload="metadata"
                          onClick={(e) => e.stopPropagation()}
                          onError={(e) => {
                            console.error("Gallery video playback error:", e);
                          }}
                        />
                      </div>
                      <div className="absolute top-2 right-2 flex space-x-2">
                        <button
                          className="p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow-md hover:bg-white dark:hover:bg-gray-700 transition-colors hover:cursor-pointer opacity-0 group-hover:opacity-100"
                          onClick={(e) => handleDeleteVideo(video.id, e)}
                          title="Delete video"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-red-500 dark:text-red-400">
                            <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          className="p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow-md hover:bg-white dark:hover:bg-gray-700 transition-colors hover:cursor-pointer opacity-0 group-hover:opacity-100"
                          onClick={() => window.open(video.video_url || "", "_blank")}
                          title="Download video"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-gray-700 dark:text-gray-200"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                            />
                          </svg>
                        </button>
                      </div>
                    </>
                  ) : (
                    // Processing indicator - styled like GenerateVideoPage
                    <div
                      className="aspect-[9/16] bg-blue-100 dark:bg-blue-800/40 relative border-2 border-blue-600 dark:border-blue-500 shadow-[0_0_12px_rgba(37,99,235,0.5)] dark:shadow-[0_0_12px_rgba(59,130,246,0.4)] animate-pulse rounded-md flex flex-col items-center justify-center"
                      onContextMenu={(e) => handleContextMenu(e, video.id)}
                    >
                      <img src="./logo.svg" className="w-16 h-16 mb-3" />
                      <div className="text-gray-800 dark:text-gray-100 font-medium mb-1.5 text-center">
                        {video.status === "processing" ? "Generating..." : "Queued..."}
                      </div>
                      {video.status === "processing" && generatingVideoId === video.id && (
                        <>
                          <div className="w-24 bg-blue-500 dark:bg-blue-600/30 rounded-full h-2.5 mb-1.5">
                            <div
                              className="h-2.5 rounded-full transition-all duration-300 bg-blue-400"
                              style={{ width: `${generationProgress}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-center text-gray-700 dark:text-blue-200">
                            {generationProgress}%
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              // Empty state
              <div className="col-span-full text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  No hook videos yet. Create your first one!
                </p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-center items-center mt-6 space-x-2">
            <button
              onClick={() => setCurrentPage((curr) => Math.max(curr - 1, 1))}
              disabled={currentPage === 1}
              className={`flex items-center justify-center w-8 h-8 rounded-md ${
                currentPage === 1
                  ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              } border border-gray-300 dark:border-gray-600`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Page {currentPage}/{totalPages}
            </span>

            <button
              onClick={() =>
                setCurrentPage((curr) => Math.min(curr + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`flex items-center justify-center w-8 h-8 rounded-md ${
                currentPage === totalPages
                  ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              } border border-gray-300 dark:border-gray-600`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {showContextMenu && (
        <div
          className="fixed bg-white dark:bg-gray-800 rounded-md shadow-lg p-1 z-50 border border-gray-200 dark:border-gray-700"
          style={{
            top: `${contextMenuPosition.y}px`,
            left: `${contextMenuPosition.x}px`,
          }}
        >
          <button
            className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            onClick={(e) => {
              e.stopPropagation();
              setShowContextMenu(false);
              setJobToDelete(contextMenuPosition.jobId);
              setShowDeleteConfirmation(true);
            }}
          >
            Delete Job
          </button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
              Delete Video Job
            </h3>
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Are you sure you want to delete this video job? This action
              cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setJobToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={() => jobToDelete && deleteVideoJob(jobToDelete)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <span className="flex items-center">
                    <LoadingIcon />
                    Deleting...
                  </span>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Include AddDemoDialog component */}
      <AddDemoDialog
        isOpen={isAddDemoDialogOpen}
        onClose={() => setIsAddDemoDialogOpen(false)}
        onAddDemo={handleAddDemo}
      />
    </div>
  );
};

export default HookVideosPage;
